// @flow
/* eslint-disable no-restricted-globals */

import * as React from 'react';
import { connect } from 'react-redux';
import { Query } from 'urql';
import { featureIsEnabled } from '@ta/common.features';
import { importScript } from '@ta/platform.import';
import { cancelTTICallback, requestTTICallback } from '@ta/platform.tti';

import GetLocationInfo from './queries/GetLocationInfo.gql';

const BLUECORE_JS_URL = 'https://api.bluecore.com/triggermail.js/tripadvisor.js';
const DATA_SAMPLING_PCT = 1;
const POST_LOAD_DELAY_MS = 3000;

type CommonProps = {|
  browserFamily: string,
  loggedInUserId: string,
  servletName: string,
  locale: string,
  geoId: number,
  detailId: number,
|};

type Props = {|
  ...CommonProps,
  geoLocalizedName: string,
|};

type LoaderEvent =
  | 'LOADER_MOUNTED'
  | 'LOAD_STARTED'
  | 'LOAD_COMPLETED'
  | 'LOAD_ERROR'
  | 'POST_LOAD';

type LoaderTiming = {|
  event: LoaderEvent,
  time: number,
|};

type AuditPayload = {|
  data: ?string,
|};

type MetricsPayload = {|
  browser: string,
  page: string,
  timings: LoaderTiming[],
|};

const addEventTiming = (payload: MetricsPayload, event: LoaderEvent) => {
  if (window.performance && window.performance.now) {
    payload.timings.push({ event, time: performance.now() });
  }
};

/**
 * Loads the core Bluecore JS file asynchronously on page idle (interactive in supported
 * browsers). The Bluecore JS adds a triggermail object to window, which contains all the
 * functions necessary for pulling relevant data from the current page. Any data we need
 * to pass to Bluecore should be put into the window.triggermail.data object.
 *
 * Metrics are queued up at each stage of the loader, then sent asynchronously on page
 * unload via sendBeacon to avoid slowing down page navigation.
 *
 * For auditing, we add the taCallback function to window.triggermail, which crm
 * will call with the data they send to their servers. The callback is a no-op unless
 * the bluecore_js_data_audit feature is enabled. Also while the feature is enabled,
 * we only record DATA_SAMPLING_PCT of requests.
 */
const BluecoreLoader = ({
  browserFamily,
  loggedInUserId,
  servletName,
  locale,
  geoId,
  geoLocalizedName,
  detailId,
}: Props) => {
  const initialized = React.useRef<boolean>(false);

  const auditPayload = React.useRef<AuditPayload>({
    data: null,
  });

  const metricsPayload = React.useRef<MetricsPayload>({
    browser: browserFamily,
    page: servletName,
    timings: [],
  });

  React.useEffect(() => {
    const unloadListener = () => {
      try {
        if (window.navigator && window.navigator.sendBeacon) {
          if (auditPayload.current.data) {
            window.navigator.sendBeacon(
              '/data/1.0/crmJSLoad/audit',
              JSON.stringify(auditPayload.current),
            );
          }
          window.navigator.sendBeacon(
            '/data/1.0/crmJSLoad/metrics',
            JSON.stringify(metricsPayload.current),
          );
        }
        // eslint-disable-next-line no-empty
      } catch (e) {}
    };

    window.addEventListener('unload', unloadListener);

    const callback = () => {
      addEventTiming(metricsPayload.current, 'LOAD_STARTED');
      importScript(BLUECORE_JS_URL, 'CRM')
        .then(() => {
          addEventTiming(metricsPayload.current, 'LOAD_COMPLETED');

          setTimeout(() => {
            addEventTiming(metricsPayload.current, 'POST_LOAD');
          }, POST_LOAD_DELAY_MS);

          if (window.triggermail) {
            window.triggermail.data = {
              bluecore_email_address: loggedInUserId,
              locale,
              geoId,
              geoLocalizedName,
              detailId,
            };
            window.triggermail.ta_callback = (data: Object) => {
              if (
                featureIsEnabled('bluecore_js_data_audit') &&
                Math.random() * 100 <= DATA_SAMPLING_PCT
              ) {
                auditPayload.current.data = JSON.stringify(data);
              }
            };
          }
        })
        .catch(() => {
          addEventTiming(metricsPayload.current, 'LOAD_ERROR');
        });
    };

    let idleCallbackHandle;
    let ttiCallbackHandle;

    if (!initialized.current) {
      initialized.current = true;
      addEventTiming(metricsPayload.current, 'LOADER_MOUNTED');

      ttiCallbackHandle = requestTTICallback(tti => {
        // Until TTI is supported in all browsers, we need to fall-back to requestIdleCallback.
        // tti will immediately resolve to null for non-supported browsers
        if (tti) {
          callback();
        } else {
          idleCallbackHandle = requestIdleCallback(callback);
        }
      });
    } else if (window.triggermail) {
      // Loader already mounted and everything initialized but userId has changed (user logged in)
      window.triggermail.data = {
        bluecore_email_address: loggedInUserId,
        locale,
        geoId,
        geoLocalizedName,
        detailId,
      };
    }

    return () => {
      window.removeEventListener('unload', unloadListener);

      if (idleCallbackHandle) {
        cancelIdleCallback(idleCallbackHandle);
      }

      if (ttiCallbackHandle) {
        cancelTTICallback(ttiCallbackHandle);
      }
    };
  }, [loggedInUserId, locale, geoId, geoLocalizedName, detailId]);
  return null;
};

/**
 * This component just queries graphql and delegates the rest to the BluecoreLoader component.
 */
const QueriedBluecoreLoader = (props: CommonProps) =>
  featureIsEnabled('bluecore_js_query') && !!props.geoId ? (
    <Query query={GetLocationInfo} variables={{ locationId: props.geoId }}>
      {({ fetching, error, data }) =>
        // If we re-render the bluecore loader before the callback fires, we cancel the script load and don't try again
        // This was always a problem but is more pronounced when the query fires.
        // For now, don't try to render the bluecore loader until we're done fetching (either successfully or we encounter an error)
        // A better solution might be to harden the bluecore loader against this kind of thing but all I have right now is nano
        // and that's a slightly bigger job.
        !fetching || error ? (
          <BluecoreLoader {...props} geoLocalizedName={data?.locations?.[0]?.name || ''} />
        ) : null
      }
    </Query>
  ) : (
    <BluecoreLoader {...props} geoLocalizedName="" />
  );

const mapStateToProps = state => ({
  browserFamily: state.meta.device.browser.family,
  loggedInUserId: state.auth.loggedInUserId,
  servletName: state.meta.initialServletName,
  locale: state.i18n.locale,
  geoId: state.page.geoId,
  detailId: state.page.detailId,
});

export default connect<CommonProps, {||}, _, _, _, _>(mapStateToProps)(QueriedBluecoreLoader);
